import { isDefined } from "@clipboard-health/util-ts";
import { Box, List } from "@mui/material";
import { Text } from "@src/appV2/redesign/components/Text";
import { type Agent } from "@src/appV2/User/api/useSearchAgents";
import { type Facility } from "@src/appV2/User/api/useSearchFacilities";
import { useEffect, useRef } from "react";

import { type WorkerShift } from "./api/useGetWorkerShifts";
import { type WorkerStripeAccount } from "./api/useGetWorkerStripeAccount";
import { type WorkplaceShift } from "./api/useGetWorkplaceShifts";
import { type ShiftSearchResult } from "./api/useSearchShift";
import { AgentResultItem } from "./components/AgentResultItem";
import { ExpandedWorkerContent } from "./components/ExpandedWorkerContent";
import { FacilityResultItem } from "./components/FacilityResultItem";
import { SectionHeader } from "./components/SectionHeader";
import { type ShiftAction, ShiftActionItem } from "./components/ShiftActionItem";
import { ShiftResultItem } from "./components/ShiftResultItem";
import { WorkplaceShiftItem } from "./components/WorkplaceShiftItem";
import { type SearchResultItem } from "./hooks/useOmnisearchResults";

export type CopiedItemType = ShiftAction | "shift" | "stripe" | "agent" | "facility";

interface OmnisearchResultsListProps {
  agents: Agent[];
  facilities: Facility[];
  shift: ShiftSearchResult | null;
  expandedShiftId: string | null;
  expandedWorkerId: string | null;
  expandedWorkplaceId: string | null;
  workerShifts: WorkerShift[];
  workerStripeAccount: WorkerStripeAccount | undefined;
  isLoadingWorkerShifts: boolean;
  isLoadingWorkerStripeAccount: boolean;
  workerHasShifts: boolean;
  workerHasStripeAccount: boolean;
  workerImpersonation: { canImpersonate: boolean; disabledReason?: string };
  workplaceShifts: WorkplaceShift[];
  isLoadingWorkplaceShifts: boolean;
  workplaceHasShifts: boolean;
  selectedIndex: number;
  copiedItem: CopiedItemType | null;
  onSelect: (item: SearchResultItem) => void;
}

export function OmnisearchResultsList(props: OmnisearchResultsListProps) {
  const {
    agents,
    facilities,
    shift,
    expandedShiftId,
    expandedWorkerId,
    expandedWorkplaceId,
    workerShifts,
    workerStripeAccount,
    isLoadingWorkerShifts,
    isLoadingWorkerStripeAccount,
    workerHasShifts,
    workerHasStripeAccount,
    workerImpersonation,
    workplaceShifts,
    isLoadingWorkplaceShifts,
    workplaceHasShifts,
    selectedIndex,
    copiedItem,
    onSelect,
  } = props;
  const isShiftExpanded = isDefined(shift) && expandedShiftId === shift.id;
  const itemRefs = useRef<Map<number, HTMLElement>>(new Map());
  useEffect(() => {
    const element = itemRefs.current.get(selectedIndex);
    element?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [selectedIndex]);
  const registerRef = (index: number) => (element: HTMLElement | null) => {
    if (element) {
      itemRefs.current.set(index, element);
    } else {
      itemRefs.current.delete(index);
    }
  };

  const getExpandedWorkerItemCount = (): number =>
    workerShifts.length + (workerHasStripeAccount ? 1 : 0) + 1; // +1 for impersonate item

  const getAgentResultIndex = (agentIndex: number): number => {
    let index = 0;
    for (let agentNumber = 0; agentNumber < agentIndex; agentNumber++) {
      index +=
        1 + (expandedWorkerId === agents[agentNumber].userId ? getExpandedWorkerItemCount() : 0);
    }
    return index;
  };

  const getFacilitiesBaseIndex = (): number =>
    agents.length + (isDefined(expandedWorkerId) ? getExpandedWorkerItemCount() : 0);

  const getFacilityResultIndex = (facilityIndex: number): number => {
    let index = getFacilitiesBaseIndex();
    for (let facilityNumber = 0; facilityNumber < facilityIndex; facilityNumber++) {
      index +=
        1 +
        (expandedWorkplaceId === facilities[facilityNumber].userId ? workplaceShifts.length : 0);
    }
    return index;
  };

  const shiftBaseIndex =
    getFacilitiesBaseIndex() +
    facilities.length +
    (isDefined(expandedWorkplaceId) ? workplaceShifts.length : 0);

  return (
    <List sx={{ py: 0 }}>
      {agents.length > 0 && (
        <>
          <SectionHeader label="Professionals" />
          {agents.map((agent, agentIndex) => {
            const isExpanded = expandedWorkerId === agent.userId;
            const resultIndex = getAgentResultIndex(agentIndex);
            return (
              <Box key={agent.userId}>
                <AgentResultItem
                  ref={registerRef(resultIndex)}
                  agent={agent}
                  copied={copiedItem === "agent" && selectedIndex === resultIndex}
                  expanded={isExpanded}
                  isLoadingShifts={isLoadingWorkerShifts || isLoadingWorkerStripeAccount}
                  selected={selectedIndex === resultIndex}
                  onClick={() => {
                    onSelect({ type: "agent", data: agent, expanded: isExpanded });
                  }}
                />
                {isExpanded && (
                  <ExpandedWorkerContent
                    agent={agent}
                    canImpersonateWorker={workerImpersonation.canImpersonate}
                    copiedItem={copiedItem}
                    impersonateDisabledReason={workerImpersonation.disabledReason}
                    isLoadingWorkerShifts={isLoadingWorkerShifts}
                    isLoadingWorkerStripeAccount={isLoadingWorkerStripeAccount}
                    registerRef={registerRef}
                    resultIndex={resultIndex}
                    selectedIndex={selectedIndex}
                    workerHasShifts={workerHasShifts}
                    workerHasStripeAccount={workerHasStripeAccount}
                    workerId={agent.userId}
                    workerShifts={workerShifts}
                    workerStripeAccount={workerStripeAccount}
                    onSelect={onSelect}
                  />
                )}
              </Box>
            );
          })}
        </>
      )}
      {facilities.length > 0 && (
        <>
          <SectionHeader hasTopMargin={agents.length > 0} label="Workplaces" />
          {facilities.map((facility, facilityIndex) => {
            const isExpanded = expandedWorkplaceId === facility.userId;
            const resultIndex = getFacilityResultIndex(facilityIndex);
            return (
              <Box key={facility.userId}>
                <FacilityResultItem
                  ref={registerRef(resultIndex)}
                  copied={copiedItem === "facility" && selectedIndex === resultIndex}
                  expanded={isExpanded}
                  facility={facility}
                  isLoadingShifts={isLoadingWorkplaceShifts}
                  selected={selectedIndex === resultIndex}
                  onClick={() => {
                    onSelect({ type: "facility", data: facility, expanded: isExpanded });
                  }}
                />
                {isExpanded && !isLoadingWorkplaceShifts && workplaceHasShifts && (
                  <>
                    {workplaceShifts.map((workplaceShift, shiftIndex) => {
                      const shiftResultIndex = resultIndex + 1 + shiftIndex;
                      return (
                        <WorkplaceShiftItem
                          key={workplaceShift.id}
                          ref={registerRef(shiftResultIndex)}
                          copied={copiedItem === "shift" && selectedIndex === shiftResultIndex}
                          selected={selectedIndex === shiftResultIndex}
                          shift={workplaceShift}
                          onClick={() => {
                            onSelect({
                              type: "workplace-shift",
                              data: workplaceShift,
                              workplaceId: facility.userId,
                            });
                          }}
                        />
                      );
                    })}
                  </>
                )}
                {isExpanded && !isLoadingWorkplaceShifts && !workplaceHasShifts && (
                  <Text color="text.secondary" sx={{ pl: 6, pr: 2, py: 1 }} variant="caption">
                    No recent or upcoming shifts
                  </Text>
                )}
              </Box>
            );
          })}
        </>
      )}
      {isDefined(shift) && (
        <>
          <SectionHeader hasTopMargin={agents.length > 0 || facilities.length > 0} label="Shifts" />
          <ShiftResultItem
            ref={registerRef(shiftBaseIndex)}
            expanded={isShiftExpanded}
            selected={selectedIndex === shiftBaseIndex}
            shift={shift}
            onClick={() => {
              onSelect({ type: "shift", data: shift, expanded: isShiftExpanded });
            }}
          />
          {isShiftExpanded && (
            <>
              {isDefined(shift.agent?.userId) && (
                <ShiftActionItem
                  ref={registerRef(shiftBaseIndex + 1)}
                  action="worker"
                  copied={copiedItem === "worker"}
                  selected={selectedIndex === shiftBaseIndex + 1}
                  shift={shift}
                  onClick={() => {
                    onSelect({ type: "shift-action", data: shift, action: "worker" });
                  }}
                />
              )}
              {isDefined(shift.facility?.userId) && (
                <ShiftActionItem
                  ref={registerRef(shiftBaseIndex + 1 + (isDefined(shift.agent?.userId) ? 1 : 0))}
                  action="workplace"
                  copied={copiedItem === "workplace"}
                  selected={
                    selectedIndex === shiftBaseIndex + 1 + (isDefined(shift.agent?.userId) ? 1 : 0)
                  }
                  shift={shift}
                  onClick={() => {
                    onSelect({ type: "shift-action", data: shift, action: "workplace" });
                  }}
                />
              )}
            </>
          )}
        </>
      )}
    </List>
  );
}
